import {Icon} from '@ezcater/tapas';
import {faBuilding, faLocationDot} from '@fortawesome/pro-regular-svg-icons';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import {twMerge} from 'tailwind-merge';

import {type Address} from '@/utils/googleAutocomplete';

type WhereAddressOptionProps = {
  address: Address;
  id: string;
  isActive?: boolean;
  onClick: () => void;
  searchTerm: string;
};

const WhereAddressOption: React.FC<WhereAddressOptionProps> = ({
  address,
  id,
  isActive = false,
  onClick,
  searchTerm,
}) => {
  const isSaved = !address.isAutocompleteResult;
  const icon = isSaved ? faBuilding : faLocationDot;

  const matches = isSaved ? [] : match(address.mainText, searchTerm, {insideWords: true});
  const parts = isSaved ? [] : parse(address.mainText, matches);

  return (
    <button
      aria-selected={isActive}
      className={twMerge(
        'flex w-full cursor-pointer items-center gap-3 rounded-lg px-2 py-2 hover:bg-peppercorn-50',
        isActive && 'bg-peppercorn-50',
      )}
      data-testid="where-address-option"
      id={id}
      onClick={onClick}
      onMouseDown={event => event.preventDefault()}
      role="option"
      tabIndex={-1}
      type="button"
    >
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-peppercorn-50 text-peppercorn-600">
        <Icon aria-hidden icon={icon} size="small" />
      </span>
      <span className="min-w-0 overflow-hidden text-left">
        {isSaved ? (
          <span className="block truncate text-sm font-semibold text-peppercorn-800">
            {address.mainText}
          </span>
        ) : (
          <span className="block truncate text-sm text-peppercorn-800">
            {parts.map((part, index) => (
              <span className={part.highlight ? 'font-bold' : 'font-normal'} key={index}>
                {part.text}
              </span>
            ))}
          </span>
        )}
        <span className="block truncate text-sm text-peppercorn-500">{address.secondaryText}</span>
      </span>
    </button>
  );
};

export default WhereAddressOption;
